import { CHORD_CATEGORIES, CHORD_TYPES, type ChordTypeId } from '@/music/chords';
import { ENHARMONIC_CHOICES, pitchClassOf } from '@/music/notes';

/**
 * Option values are the canonical (sharp) spelling, but callers hand us
 * whatever the chart says — "Bb", "Eb"… A controlled select whose value
 * matches no option renders blank, so normalise by pitch class.
 */
function canonicalSpelling(value: string): string {
  if (!value) return value;
  try {
    const pc = pitchClassOf(value);
    return ENHARMONIC_CHOICES.find((c) => c.pc === pc)?.spellings[0] ?? value;
  } catch {
    return value;
  }
}

interface NoteSelectProps {
  value: string;
  onChange: (value: string) => void;
  /** Use the sharp spelling as the option value (default) for engine calls. */
  id?: string;
  includeBlank?: boolean;
  blankLabel?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

export function NoteSelect({
  value,
  onChange,
  id,
  includeBlank,
  blankLabel = '—',
  disabled,
  'aria-label': ariaLabel,
}: NoteSelectProps) {
  return (
    <select
      id={id}
      value={canonicalSpelling(value)}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      {includeBlank && <option value="">{blankLabel}</option>}
      {ENHARMONIC_CHOICES.map((choice) => (
        <option key={choice.pc} value={choice.spellings[0]}>
          {choice.display}
        </option>
      ))}
    </select>
  );
}

interface ChordTypeSelectProps {
  value: ChordTypeId;
  onChange: (value: ChordTypeId) => void;
  id?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

export function ChordTypeSelect({
  value,
  onChange,
  id,
  disabled,
  'aria-label': ariaLabel,
}: ChordTypeSelectProps) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as ChordTypeId)}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      {CHORD_CATEGORIES.map((category) => (
        <optgroup key={category.category} label={category.label}>
          {category.types.map((id) => (
            <option key={id} value={id}>
              {CHORD_TYPES[id].name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
